import {Switch} from '@material-ui/core';
import {format} from 'date-fns';
import {Spinner} from 'evergreen-ui';
import React from 'react';
import {useCurrentUser} from 'security';
import {ConversationTextArea} from './ConversationTextArea';
import type {Message, MessageSender, OfferSummary} from './types';

const MAX_MESSAGE_LENGTH = 1000;

export const ChatIcon = () => (
    <svg width={20} height={20} viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
        <g fill="currentColor" fillRule="nonzero" stroke="none">
            <g transform="scale(4,4)">
                <path d="M32,10c13.785,0 25,8.951 25,19.954c0,11.003 -11.215,19.953 -25,19.953c-0.646,0 -1.311,-0.022 -1.983,-0.065c-7.369,5.504 -13.411,6.251 -13.672,6.281c-0.076,0.009 -0.152,0.013 -0.228,0.013c-0.7,0 -1.356,-0.368 -1.72,-0.979c-0.402,-0.678 -0.369,-1.528 0.084,-2.172c0.023,-0.033 1.966,-2.8 4.003,-6.234c-7.138,-3.647 -11.484,-9.924 -11.484,-16.797c0,-11.003 11.215,-19.954 25,-19.954z"/>
            </g>
        </g>
    </svg>
);

type SenderIdentity = {
    name: string;
    email: string | null | undefined;
};

const getSenderIdentity = (
    message: Message,
    currentUser: ReturnType<typeof useCurrentUser>,
    senderCache: Map<string, MessageSender>
): SenderIdentity => {
    const isCurrentUser = message.senderGuid === currentUser?.guid;
    const sender = message.sender ?? senderCache.get(message.senderGuid);
    return {
        name: isCurrentUser ? `${currentUser?.firstName} ${currentUser?.lastName} (You)` : (sender ? `${sender.firstName} ${sender.lastName}`.trim() : 'Loading...'),
        email: sender?.email ?? (isCurrentUser ? currentUser?.email : null),
    };
};

type MessageListItemProps = {
    message: Message;
    currentUser: ReturnType<typeof useCurrentUser>;
    senderCache: Map<string, MessageSender>;
};

const MessageListItem = ({message, currentUser, senderCache}: MessageListItemProps) => {
    if (message.messageType !== 'TEXT') {
        return (
            <div className="text-center text-xs text-gray-500 py-1">
                {message.content} · {format(new Date(message.createdAt), 'MMM d, h:mm a')}
            </div>
        );
    }

    const isCurrentUser = message.senderGuid === currentUser?.guid;
    const senderIdentity = getSenderIdentity(message, currentUser, senderCache);
    return (
        <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}>
            <span className="text-xs text-white mb-2 px-1 text-center">
                {senderIdentity.name}
                {message.internal && <span className="ml-2 inline-flex items-center px-1 py-0.5 text-xs font-medium bg-red-600 text-white">CCS</span>}
                {!message.internal && <span className="ml-2 inline-flex items-center px-1 py-0.5 text-xs font-medium text-white" style={{backgroundColor: '#2E6DF5'}}>PROVIDER</span>}
            </span>
            <div className="max-w-[85%] p-2 text-sm text-white" style={{backgroundColor: message.internal ? '#4E7A4B' : '#2E6DF5'}}>
                <div className="text-sm whitespace-pre-wrap break-words">{message.content}</div>
                <div className="text-xs text-gray-400 mt-1">{format(new Date(message.createdAt), 'MMM d, h:mm a')}</div>
            </div>
        </div>
    );
};

type ConversationMessageListProps = {
    messages: Message[];
    isLoading: boolean;
    isLoadingOlder: boolean;
    currentUser: ReturnType<typeof useCurrentUser>;
    senderCache: Map<string, MessageSender>;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    messagesContainerRef: React.RefObject<HTMLDivElement>;
    onScroll: React.UIEventHandler<HTMLDivElement>;
    unreadDividerIndex: number;
    isMessagingConnected: boolean;
    hasConversation: boolean;
    unreadCount: number;
};

const UnreadDivider = () => (
    <div className="flex items-center gap-2 py-1">
        <div className="h-px flex-1 bg-gray-600"/>
        <span className="text-xs text-gray-300">New / unread</span>
        <div className="h-px flex-1 bg-gray-600"/>
    </div>
);

export const ConversationMessageList = ({messages, isLoading, isLoadingOlder, currentUser, senderCache, messagesEndRef, messagesContainerRef, onScroll, unreadDividerIndex, isMessagingConnected, hasConversation, unreadCount}: ConversationMessageListProps) => (
    <div ref={messagesContainerRef} onScroll={onScroll} className="flex-grow overflow-y-auto p-3 space-y-2 bg-gray-900 chat-scroll">
        {isLoadingOlder && <div className="flex justify-center py-2"><Spinner size={16}/></div>}
        {isLoading && messages.length === 0 && <div className="flex justify-center py-4"><Spinner size={24}/></div>}
        {!isLoading && messages.length === 0 && <div className="text-white text-sm text-center py-4">No messages yet. Start the conversation.</div>}
        {unreadCount > 0 && unreadDividerIndex === -1 && messages.length > 0 && <UnreadDivider/>}
        {messages.map((message, index) => (
            <React.Fragment key={message.guid}>
                <MessageListItem message={message} currentUser={currentUser} senderCache={senderCache}/>
                {unreadCount > 0 && index === unreadDividerIndex && <UnreadDivider/>}
            </React.Fragment>
        ))}
        <div ref={messagesEndRef}/>
    </div>
);

type ConversationHeaderProps = {
    urgent: boolean;
    hasConversation: boolean;
    offerLoading: boolean;
    isReferralProviderReady: boolean;
    offer?: OfferSummary;
    isInternalAuth: boolean;
    readOnly: boolean;
    unreadCount: number;
    isMarkingMessages: boolean;
    canToggleRead: boolean;
    canMarkAsUnread: boolean;
    onBack?: () => void;
    onClose: () => void;
    onToggleUrgent: () => void;
    onMarkAsRead: () => void;
    onMarkAsUnread: () => void;
};

export const ConversationHeader = ({urgent, hasConversation, offerLoading, isReferralProviderReady, offer, isInternalAuth, readOnly, unreadCount, isMarkingMessages, canToggleRead, canMarkAsUnread, onBack, onClose, onToggleUrgent, onMarkAsRead, onMarkAsUnread}: ConversationHeaderProps) => (
    <div className={`flex justify-between items-start px-4 py-2 rounded-t-lg ${urgent ? 'bg-red-600' : 'bg-blue-600'}`}>
        <div className="pr-4 text-white">
            <h3 id="referral-communication-title" className="font-semibold text-base">Referral Communication</h3>
            {offerLoading ? <div className="mt-1 text-xs text-blue-100">Loading referral details...</div> : <div className="mt-1 space-y-0.5 text-xs text-white"><div>Referral {offer?.referral?.caseNumber ?? '—'} · {offer?.referral?.clientName ?? '—'}</div><div>Provider: {offer?.provider?.name ?? '—'}</div></div>}
        </div>
        <div className="flex items-center gap-3">
            {onBack && <button type="button" onClick={onBack} className="text-xs font-medium text-white hover:text-gray-200">Back</button>}
            {hasConversation && <>{urgent && !isInternalAuth && 
                <span className="text-xs font-medium text-yellow-200">Urgent</span>}
                {isInternalAuth && !readOnly && 
                    <label className="flex items-center gap-1 text-xs font-medium text-white cursor-pointer"><span>Urgent</span>
                        <Switch checked={urgent} onChange={onToggleUrgent} disabled={!isReferralProviderReady} size="small" color="default"/>
                    </label>}
            </>}
            {canToggleRead && <button type="button" onClick={unreadCount > 0 ? onMarkAsRead : onMarkAsUnread} disabled={isMarkingMessages || (unreadCount === 0 && !canMarkAsUnread)} className="text-xs focus:outline-none font-medium text-white hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">{unreadCount > 0 ? 'Mark as read' : 'Mark as unread'}</button>}
            <button type="button" onClick={onClose} className="text-white hover:text-gray-200 text-xl leading-none focus:outline-none" aria-label="Close referral communication">&times;</button>
        </div>
    </div>
);

type ConversationInputProps = {
    input: string;
    setInput: (value: string) => void;
    readOnly: boolean;
    isSendingMessage: boolean;
    isReferralProviderReady: boolean;
    onSend: () => void;
    onClose: () => void;
};

export const ConversationInput = ({input, setInput, readOnly, isSendingMessage, isReferralProviderReady, onSend, onClose}: ConversationInputProps) => {
    const isSendDisabled = readOnly || !isReferralProviderReady || !input.trim() || isSendingMessage;
    return (
        <div className="p-3 border-t border-gray-700 bg-gray-900">
            <ConversationTextArea
                value={input}
                onChange={setInput}
                onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (!readOnly && !isSendingMessage && isReferralProviderReady && event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        onSend();
                    }
                }}
                placeholder={readOnly ? 'Communication is read-only' : 'Type a message...'}
                disabled={isSendingMessage || readOnly}
                maxLength={MAX_MESSAGE_LENGTH}
                rows={1}
                maxRows={3}
                className="w-full"
                variant="dark"
            />
            <div className="flex justify-between items-center mt-2">
                <div className="text-left text-xs text-gray-300">
                    {input.length}/{MAX_MESSAGE_LENGTH} characters
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={onClose} 
                        className="px-4 py-1.5 rounded-none text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 focus:outline-none w-24">
                        Close
                    </button>
                    <button type="button" onClick={onSend} disabled={isSendDisabled} 
                        className="px-4 py-1.5 rounded-none text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none w-24"
                        style={{backgroundColor: '#2E6DF5'}}>
                        {isSendingMessage ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
        </div>
    );
};

type ConversationTriggerProps = {
    urgent: boolean;
    unreadCount: number;
    hasConversation: boolean;
    onOpen: () => void;
    colorClass: string;
};

export const ConversationTrigger = ({urgent, unreadCount, hasConversation, onOpen, colorClass}: ConversationTriggerProps) => {
    const label = urgent ? `Urgent referral communication${unreadCount > 0 ? `, ${unreadCount} unread` : ''}` : unreadCount > 0 ? `${unreadCount} unread referral communication${unreadCount === 1 ? '' : 's'}` : hasConversation ? 'Referral communication, no unread messages' : 'Start referral communication';
    return <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }} className={`inline-flex min-w-12 items-center justify-center gap-1 rounded px-2 py-1 transition-colors focus:outline-none ${colorClass}`} aria-label={label} title={label}><ChatIcon/>{unreadCount > 0 && <span className="text-sm font-semibold">{unreadCount}</span>}</button>;
};
